import {
  INTRANET_FILE_ACCEPT,
  INTRANET_FILE_ALLOWED_MIME,
  INTRANET_FILE_MAX_BYTES,
  INTRANET_IMAGE_MAX_BYTES,
  intranetImageMaxMb,
} from '@gb-ticket/shared-types';
import { type IntranetFile } from '@gb-ticket/shared-types';
import { Loader2Icon, ThumbsDownIcon, ThumbsUpIcon, UploadIcon } from 'lucide-react';
import { type ChangeEvent, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useIntranetAppearance } from '@/products/intranet/features/intranet/use-intranet';
import {
  useIntranetFeedbackNotes,
  useIntranetNotices,
  useUploadIntranetFile,
} from '@/products/intranet/features/intranet/use-intranet-library';

const ALLOWED_MIME = new Set<string>(INTRANET_FILE_ALLOWED_MIME);
const LIMITED_IMAGE_MIME = new Set(['image/jpeg', 'image/png', 'image/webp']);

/** Editorial tools on a library entry: upload a new version, read notes left by readers. */
export function EditorLibraryPanel({
  slug,
  files,
}: {
  slug: string;
  files: readonly IntranetFile[];
}) {
  const appearance = useIntranetAppearance();
  const upload = useUploadIntranetFile(slug);
  const notes = useIntranetFeedbackNotes(slug, true);
  const notices = useIntranetNotices(slug, true);
  const input = useRef<HTMLInputElement>(null);
  const [replacesFileId, setReplacesFileId] = useState('');
  const currents = files.filter((file) => file.isCurrent);
  const contentImageMaxBytes = appearance.data?.contentImageMaxBytes ?? INTRANET_IMAGE_MAX_BYTES;
  const contentImageMaxMb = intranetImageMaxMb(contentImageMaxBytes);
  const knowledgeFileMaxBytes = appearance.data?.knowledgeFileMaxBytes ?? INTRANET_FILE_MAX_BYTES;
  const knowledgeFileMaxMb = intranetImageMaxMb(knowledgeFileMaxBytes);

  async function onFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    const refreshed = await appearance.refetch();
    const imageLimit = refreshed.data?.contentImageMaxBytes ?? INTRANET_IMAGE_MAX_BYTES;
    const fileLimit = refreshed.data?.knowledgeFileMaxBytes ?? INTRANET_FILE_MAX_BYTES;
    if (LIMITED_IMAGE_MIME.has(file.type)) {
      if (file.size > imageLimit) {
        toast.error(`Das Bild darf höchstens ${intranetImageMaxMb(imageLimit)} MB groß sein.`);
        return;
      }
    } else if (file.size > fileLimit) {
      toast.error(`Die Datei darf höchstens ${intranetImageMaxMb(fileLimit)} MB groß sein.`);
      return;
    }
    if (file.type && !ALLOWED_MIME.has(file.type)) {
      toast.error('Dieser Dateityp ist nicht erlaubt.');
      return;
    }
    try {
      const saved = await upload.mutateAsync({
        file,
        replacesFileId: replacesFileId || undefined,
      });
      setReplacesFileId('');
      toast.success(
        replacesFileId
          ? `Neue Version von „${currents.find((item) => item.id === replacesFileId)?.filename ?? 'Dokument'}“ hochgeladen.`
          : `„${saved.filename}“ hinzugefügt.`,
      );
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Hochladen fehlgeschlagen.');
    }
  }

  const items = notes.data?.items ?? [];
  const meldungen = notices.data?.items ?? [];

  return (
    <section
      aria-labelledby="redaktion"
      className="space-y-4 rounded-xl border border-border bg-card p-4 shadow-sm sm:p-5"
    >
      <h2 id="redaktion" className="font-semibold text-primary">
        Redaktion
      </h2>
      <div className="space-y-2">
        <input
          ref={input}
          type="file"
          className="sr-only"
          accept={INTRANET_FILE_ACCEPT}
          aria-label="Datei auswählen"
          onChange={(event) => void onFile(event)}
        />
        {currents.length > 0 ? (
          <label className="flex max-w-sm flex-col gap-1 text-sm">
            <span className="text-muted-foreground">Neue Version für</span>
            <Select
              value={replacesFileId || 'none'}
              onValueChange={(value) => setReplacesFileId(value === 'none' ? '' : value)}
            >
              <SelectTrigger aria-label="Neue Version für">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Weiteres Dokument</SelectItem>
                {currents.map((file) => (
                  <SelectItem key={file.id} value={file.id}>
                    {file.filename}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        ) : null}
        <Button
          type="button"
          variant="outline"
          disabled={upload.isPending}
          onClick={() => {
            void appearance.refetch().finally(() => input.current?.click());
          }}
        >
          {upload.isPending ? (
            <Loader2Icon className="animate-spin" aria-hidden />
          ) : (
            <UploadIcon aria-hidden />
          )}
          {replacesFileId ? 'Neue Version hochladen' : 'Dokument hinzufügen'}
        </Button>
        <p className="text-xs text-muted-foreground">
          PDF, Office-Dokumente, Text, Video oder ZIP, höchstens {knowledgeFileMaxMb} MB. JPEG, PNG
          und WebP höchstens {contentImageMaxMb} MB. Ältere Versionen bleiben abrufbar.
        </p>
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-medium">Meldungen</h3>
        {notices.data?.responsible ? (
          <p className="text-sm text-muted-foreground">
            Zuständige Person: {notices.data.responsible.displayName}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">Zuständige Person ist nicht gesetzt.</p>
        )}
        {notices.isPending ? (
          <p className="text-sm text-muted-foreground">Wird geladen …</p>
        ) : meldungen.length === 0 ? (
          <p className="text-sm text-muted-foreground">Noch keine Meldungen.</p>
        ) : (
          <ul className="space-y-2">
            {meldungen.map((item) => (
              <li key={item.id} className="rounded-md bg-muted/40 px-3 py-2 text-sm">
                <p className="text-xs text-muted-foreground">
                  {item.author.displayName} · {formatDateTime(item.createdAt)}
                  {item.recipients.length > 0
                    ? ` · an ${item.recipients.map((person) => person.displayName).join(', ')}`
                    : ''}
                </p>
                <p className="mt-1 whitespace-pre-wrap">{item.text}</p>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-medium">Rückmeldungen der Leser</h3>
        {notes.isPending ? (
          <p className="text-sm text-muted-foreground">Wird geladen …</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">Noch keine schriftlichen Rückmeldungen.</p>
        ) : (
          <ul className="space-y-2">
            {items.map((item) => (
              <li key={item.id} className="rounded-md bg-muted/40 px-3 py-2 text-sm">
                <p className="flex items-center gap-2 text-xs text-muted-foreground">
                  {item.helpful ? (
                    <ThumbsUpIcon className="size-3.5" aria-label="hilfreich" />
                  ) : (
                    <ThumbsDownIcon className="size-3.5" aria-label="nicht hilfreich" />
                  )}
                  {item.author.displayName} · {formatDateTime(item.updatedAt)}
                </p>
                <p className="mt-1 whitespace-pre-wrap">{item.note}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
